/**
 * <nwf-payment-tiles>
 *
 * Explanatory popovers on the payment tiles of the [NWF] Advantages section.
 *
 * The legacy shop opened these on hover only. That is fine for a one-line hint
 * but not for what they actually contain here - the CreditPlus financing terms
 * run several paragraphs, and the Vorkasse tile holds an IBAN and BIC that a
 * customer has to be able to select and copy. A popover that collapses the
 * moment the pointer leaves makes that impossible, and hover alone is
 * unreachable on touch and by keyboard.
 *
 * So the two mechanisms are split by concern:
 *   - Hover is pure CSS, gated behind (hover: hover) and (pointer: fine).
 *   - Click / tap toggles aria-expanded here, which wins over hover and keeps
 *     the popover open after the pointer moves away.
 *
 * Only one popover is open at a time. Escape closes and returns focus to its
 * button; a click outside closes without moving focus.
 */
if (!customElements.get('nwf-payment-tiles')) {
    customElements.define(
        'nwf-payment-tiles',
        class NwfPaymentTiles extends HTMLElement {
            connectedCallback() {
                this.toggles = [...this.querySelectorAll('.nwf-payment-tiles__tile--toggle')];
                if (!this.toggles.length) return;

                this.onClick = this.handleClick.bind(this);
                this.onKeydown = this.handleKeydown.bind(this);
                this.onPointerLeave = this.handlePointerLeave.bind(this);
                this.onDocumentClick = this.handleDocumentClick.bind(this);

                this.addEventListener('click', this.onClick);
                this.addEventListener('keydown', this.onKeydown);
                this.addEventListener('pointerleave', this.onPointerLeave, true);
                document.addEventListener('click', this.onDocumentClick);
            }

            disconnectedCallback() {
                this.removeEventListener('click', this.onClick);
                this.removeEventListener('keydown', this.onKeydown);
                this.removeEventListener('pointerleave', this.onPointerLeave, true);
                document.removeEventListener('click', this.onDocumentClick);
            }

            itemFor(toggle) {
                return toggle.closest('.nwf-payment-tiles__item');
            }

            /**
             * Collapsing while the pointer still rests on the tile would leave the
             * CSS hover rule showing a popover the user just dismissed. The marker
             * suppresses hover for that one item until the pointer actually leaves.
             */
            close(toggle, dismissed) {
                toggle.setAttribute('aria-expanded', 'false');

                const item = this.itemFor(toggle);
                if (item) item.classList.toggle('nwf-payment-tiles__item--dismissed', Boolean(dismissed));
            }

            closeAll(except) {
                this.toggles.forEach((toggle) => {
                    if (toggle !== except) this.close(toggle, false);
                });
            }

            handleClick(event) {
                const toggle = event.target.closest('.nwf-payment-tiles__tile--toggle');
                if (!toggle || !this.contains(toggle)) return;

                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                this.closeAll(toggle);

                if (isOpen) {
                    this.close(toggle, true);
                    return;
                }

                toggle.setAttribute('aria-expanded', 'true');
                const item = this.itemFor(toggle);
                if (item) item.classList.remove('nwf-payment-tiles__item--dismissed');
            }

            handleKeydown(event) {
                if (event.key !== 'Escape') return;

                const open = this.toggles.find((toggle) => toggle.getAttribute('aria-expanded') === 'true');
                if (!open) return;

                this.close(open, true);
                open.focus();
            }

            /* Capture phase: pointerleave does not bubble. */
            handlePointerLeave(event) {
                const item = event.target.closest?.('.nwf-payment-tiles__item');
                if (item) item.classList.remove('nwf-payment-tiles__item--dismissed');
            }

            handleDocumentClick(event) {
                if (this.contains(event.target)) return;
                this.closeAll();
            }
        },
    );
}
